import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api.js';
import './wallet-top-up-payment.scss';

const BLUE = { color: '#005293' };
const PRESETS = [200, 500, 1000, 2000];
const MIN_AMOUNT = 100;
const MAX_AMOUNT = 10000;
const peso = (v) => `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => new Date(iso).toLocaleString('en-PH', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });

function downloadReceipt(r) {
  const lines = [
    'ChargeOS - Payment Receipt',
    `Receipt No: ${r.receiptNumber}`,
    `Transaction: ${r.transactionReference}`,
    `Customer: ${r.customer}`,
    `Channel: PayMongo (${r.channel})`,
    `Amount: ${peso(r.amount)} ${r.currency}`,
    `Paid at: ${new Date(r.paidAt).toLocaleString('en-PH')}`,
    `Balance after: ${r.balanceAfter == null ? '-' : peso(r.balanceAfter)}`,
  ];
  const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${r.receiptNumber}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function WalletTopUpPaymentPage() {
  const [params, setParams] = useSearchParams();
  const topUpParam = params.get('topUp');
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [amount, setAmount] = useState('500');
  const [channel, setChannel] = useState('gcash');
  const [amountError, setAmountError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [checkout, setCheckout] = useState(null); // top-up awaiting authorization (mock checkout)
  const [confirmed, setConfirmed] = useState(null); // paid top-up
  const [autoSaving, setAutoSaving] = useState(false);

  const refresh = useCallback(async () => {
    const [w, l] = await Promise.all([api.getWallet(), api.walletLedger(1, 20)]);
    setWallet(w);
    setLedger(l.data);
  }, []);

  useEffect(() => {
    refresh().catch((e) => setError(e.message || 'Could not load your wallet.'));
  }, [refresh]);

  // Returning from checkout (or the mock checkout): resolve the top-up referenced in the URL.
  useEffect(() => {
    if (!topUpParam) return undefined;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const t = await api.getTopUp(topUpParam);
        if (cancelled) return;
        if (t.status === 'paid') {
          setCheckout(null);
          setConfirmed(t);
          await refresh();
        } else if (t.mock && params.get('checkout') === 'mock') {
          setCheckout(t);
        } else if (tries < 10) {
          tries += 1;
          setTimeout(poll, 1500);
        } else {
          setNotice('Your payment is still being confirmed. Your balance will update shortly.');
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [topUpParam, params, refresh]);

  const pickPreset = (v) => {
    setAmount(String(v));
    setAmountError('');
  };

  const validate = () => {
    const v = Number(amount);
    if (amount === '' || Number.isNaN(v)) return 'Please enter a valid top-up amount.';
    if (v < MIN_AMOUNT) return `Minimum top-up amount is ${peso(MIN_AMOUNT)}.`;
    if (v > MAX_AMOUNT) return `Maximum top-up amount is ${peso(MAX_AMOUNT)}.`;
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    const msg = validate();
    setAmountError(msg);
    if (msg) return;
    setSubmitting(true);
    try {
      const t = await api.createTopUp({ amount: Number(amount), channel });
      const url = new URL(t.checkoutUrl, window.location.origin);
      if (t.mock) {
        setParams({ checkout: 'mock', topUp: t.id });
        setCheckout(t);
      } else {
        window.location.assign(url.toString());
      }
    } catch (err) {
      setError(err.message || 'Could not start the payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const authorize = async () => {
    setSubmitting(true);
    setError('');
    try {
      const t = await api.simulateTopUpPayment(checkout.id);
      setCheckout(null);
      setConfirmed(t);
      setParams({ topUp: t.id });
      await refresh();
    } catch (err) {
      setError(err.message || 'Payment authorization failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const saveAuto = async (next) => {
    setAutoSaving(true);
    setError('');
    try {
      setWallet(await api.saveAutoReload(next));
    } catch (err) {
      setError(err.message || 'Could not save auto-reload settings.');
    } finally {
      setAutoSaving(false);
    }
  };

  const auto = wallet?.autoReload || { enabled: false, threshold: 200, amount: 500 };
  const autoPayload = (patch) => ({ enabled: auto.enabled, threshold: auto.threshold ?? 200, amount: auto.amount ?? 500, ...patch });

  const receipt = async () => {
    try {
      downloadReceipt(await api.getTopUpReceipt(confirmed.id));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4 align-items-center">
        <div className="col-12 col-md-8">
          <div className="d-flex align-items-center mb-1">
            <div className="rounded d-flex align-items-center justify-content-center me-3" style={{ width: 44, height: 44, background: '#e0f2fe', border: '1px solid #bae6fd' }}>
              <i className="fa fa-wallet fa-lg" style={BLUE} aria-hidden="true" />
            </div>
            <div>
              <h1 className="h3 fw-bold mb-0" style={BLUE}>Driver Prepaid Wallet</h1>
              <p className="text-muted small mb-0">Manage prepaid charging balances, top-up via PayMongo, configure auto-reload, and inspect auditable ledger entries.</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 text-md-end mt-3 mt-md-0">
          <Link to="/station-locator-availability" className="btn btn-outline-primary btn-sm">
            <i className="fa fa-charging-station me-1" aria-hidden="true" /> Go to Station Locator
          </Link>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2" id="wallet-error" role="alert">{error}</div>}
      {notice && <div className="alert alert-info py-2" id="wallet-notice" role="status">{notice}</div>}

      {confirmed && (
        <div className="alert alert-success d-flex flex-wrap justify-content-between align-items-center" id="topup-confirmation" role="status">
          <div>
            <strong><i className="fa fa-check-circle me-1" aria-hidden="true" />Payment confirmed.</strong>{' '}
            {peso(confirmed.amount)} was added to your wallet. Updated balance: <strong id="confirmed-balance">{peso(confirmed.walletBalance)}</strong>
            {confirmed.transactionReference && <> (<code>{confirmed.transactionReference}</code>)</>}
          </div>
          <button type="button" className="btn btn-sm btn-outline-success mt-2 mt-md-0" id="btn-download-receipt" onClick={receipt}>
            <i className="fa fa-download me-1" aria-hidden="true" />Download receipt
          </button>
        </div>
      )}

      {checkout && (
        <div className="card p-4 mb-4 shadow-sm border bg-white" id="mock-checkout" style={{ borderTop: '4px solid #f59e0b' }}>
          <h5 className="fw-bold mb-2" style={BLUE}><i className="fa fa-lock me-2" aria-hidden="true" />PayMongo Checkout (sandbox)</h5>
          <p className="small text-muted mb-3">
            Pay <strong>{peso(checkout.amount)}</strong> with <strong>{checkout.channelLabel}</strong>. This sandbox checkout simulates your provider authorization; no real money moves.
          </p>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-primary fw-bold" id="btn-authorize-payment" onClick={authorize} disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2" />}Authorize payment
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => { setCheckout(null); setParams({}); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12 col-lg-5 mb-4">
          <div className="card p-4 mb-4 shadow-sm border bg-white" style={{ borderTop: '4px solid #005293' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted small text-uppercase fw-bold">Available Spendable Balance</span>
              <span className="badge badge-available px-2 py-1"><i className="fa fa-check me-1" aria-hidden="true" />Active</span>
            </div>
            <div className="my-2">
              <span className="display-4 fw-bold" style={BLUE} id="display-balance">{wallet ? peso(wallet.balance) : '…'}</span>
              <span className="h5 text-muted ms-1">PHP</span>
            </div>
            <div className="d-flex justify-content-between text-muted small mt-2 pt-2 border-top">
              <span>Active Pre-Auth Holds: <strong className="text-dark">{peso(wallet?.heldBalance ?? 0)}</strong></span>
              <span>Currency: <strong className="text-dark">PHP (₱)</strong></span>
            </div>
          </div>

          <div className="card p-4 mb-4 shadow-sm border bg-white">
            <h5 className="fw-bold mb-3" style={BLUE}><i className="fa fa-plus-circle text-primary me-2" aria-hidden="true" />Instant Wallet Top-Up</h5>
            <form id="topup-form" onSubmit={submit} noValidate>
              <label className="form-label small fw-bold mb-2">Select Reload Amount</label>
              <div className="row mb-3">
                {PRESETS.map((v) => (
                  <div className="col-6 mb-2" key={v}>
                    <button type="button" className={`btn btn-outline-secondary w-100 amount-btn ${Number(amount) === v ? 'active' : ''}`} data-amount={v} onClick={() => pickPreset(v)}>
                      {`₱${v.toLocaleString('en-PH')}`}
                    </button>
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <label htmlFor="custom-amount" className="form-label small fw-bold">Or Enter Custom Amount (₱)</label>
                <div className="input-group has-validation">
                  <span className="input-group-text bg-light border-end-0">₱</span>
                  <input type="number" className={`form-control border-start-0 ${amountError ? 'is-invalid' : ''}`} id="custom-amount" min={MIN_AMOUNT} max={MAX_AMOUNT} step="50" value={amount} onChange={(e) => { setAmount(e.target.value); setAmountError(''); }} aria-invalid={!!amountError} />
                  <div className="invalid-feedback" id="amount-error">{amountError}</div>
                </div>
              </div>

              <label className="form-label small fw-bold mb-2">Select PayMongo Channel</label>
              <div className="d-flex flex-wrap mb-4">
                {[['gcash', 'GCash', 'pay-gcash'], ['maya', 'Maya', 'pay-maya'], ['card', 'Credit / Debit Card', 'pay-card']].map(([v, label, id]) => (
                  <div className="form-check me-3" key={v}>
                    <input type="radio" id={id} name="pay-channel" className="form-check-input" checked={channel === v} onChange={() => setChannel(v)} />
                    <label className="form-check-label text-dark small fw-bold" htmlFor={id}>{label}</label>
                  </div>
                ))}
              </div>

              <button type="submit" id="btn-submit-topup" className="btn btn-primary w-100 fw-bold py-2" disabled={submitting}>
                {submitting && <span id="topup-spinner" className="spinner-border spinner-border-sm me-2" />}
                <span id="topup-btn-text">{submitting ? 'Redirecting to PayMongo...' : 'Proceed to PayMongo Checkout'}</span>
              </button>
            </form>
          </div>

          <div className="card p-4 shadow-sm border bg-white">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold mb-0" style={BLUE}><i className="fa fa-rotate text-warning me-2" aria-hidden="true" />Auto-Reload Protection</h6>
              <div className="form-check form-switch">
                <input type="checkbox" className="form-check-input" role="switch" id="auto-reload-toggle" checked={auto.enabled} disabled={autoSaving || !wallet} onChange={(e) => saveAuto(autoPayload({ enabled: e.target.checked }))} />
                <label className="form-check-label visually-hidden" htmlFor="auto-reload-toggle">Auto Reload</label>
              </div>
            </div>
            <p className="text-muted small mb-3">Choose a balance threshold and a top-up amount. Your preference is saved; automatic reloads need a stored payment method and are not charged in this demo.</p>
            <div className="row">
              <div className="col-6">
                <label className="text-muted small" htmlFor="auto-threshold">When balance is below</label>
                <select className="form-select form-select-sm" id="auto-threshold" value={auto.threshold ?? 200} disabled={!auto.enabled || autoSaving} onChange={(e) => saveAuto(autoPayload({ threshold: Number(e.target.value) }))}>
                  {[100, 200, 300].map((v) => <option key={v} value={v}>{peso(v)}</option>)}
                </select>
              </div>
              <div className="col-6">
                <label className="text-muted small" htmlFor="auto-amount">Auto top-up amount</label>
                <select className="form-select form-select-sm" id="auto-amount" value={auto.amount ?? 500} disabled={!auto.enabled || autoSaving} onChange={(e) => saveAuto(autoPayload({ amount: Number(e.target.value) }))}>
                  {[500, 1000, 2000].map((v) => <option key={v} value={v}>{peso(v)}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <div className="card p-4 shadow-sm border bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0" style={BLUE}><i className="fa fa-file-invoice-dollar text-primary me-2" aria-hidden="true" />Auditable Ledger History</h5>
              <span className="badge bg-secondary small">Double-Entry Journal</span>
            </div>
            <p className="text-muted small mb-3">Every wallet credit, reservation hold, and charging session deduction is immutably recorded in the double-entry accounting ledger.</p>
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0">
                <thead>
                  <tr className="text-muted small text-uppercase">
                    <th>Date / Ref</th><th>Description</th><th>Type</th><th>Amount</th><th>Balance After</th>
                  </tr>
                </thead>
                <tbody id="ledger-rows">
                  {ledger.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted py-3">No wallet transactions yet.</td></tr>
                  )}
                  {ledger.map((r) => {
                    const credit = r.type === 'CREDIT';
                    return (
                      <tr key={r.id}>
                        <td>
                          <small className="text-muted d-block">{fmtDate(r.createdAt)}</small>
                          <code className="text-primary fw-bold small">{r.reference}</code>
                        </td>
                        <td className="fw-bold text-dark">{r.description}</td>
                        <td><span className={`badge ${credit ? 'badge-available' : 'badge-faulted'}`}>{r.type}</span></td>
                        <td className={`fw-bold ${credit ? 'text-success' : 'text-danger'}`}>{credit ? '+' : '-'} {peso(r.amount)}</td>
                        <td className="fw-bold text-dark">{r.balanceAfter == null ? '—' : peso(r.balanceAfter)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
